import { type LinksFunction, json, type MetaFunction } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { useState } from 'react'
import BackgroundBlur from '#app/components/background/bg-blur'
import { Button } from '#app/components/buttons/button'
import { Flex } from '#app/components/layout/flex'
import { H1 } from '#app/components/typography/h1'
import { Lead } from '#app/components/typography/lead'
import { ArticlePreview } from '#app/old/patterns/article-preview'
import { post as componentDesignAndBestPractices } from '#app/routes/_marketing+/articles+/_articles/component-design-and-best-practices/index'
import { post as theImportanceOfUsingAReactUiLibrary } from '#app/routes/_marketing+/articles+/_articles/the-importance-of-using-a-react-ui-library/index'

export const links: LinksFunction = () => {
	return [{ rel: 'canonical', href: '/blog' }]
}

export const meta: MetaFunction = () => {
	return [
		{ title: `Articles | VerveUI` },
		{
			name: 'description',
			content: `Articles to make full-stack web dev a cakewalk.`,
		},
		{
			name: 'keywords',
			content: `full-stack, web development, articles, tutorials, guides, web dev, front-end, back-end, javascript, typescript, react, node, css, html, web design, web development, web developer, web development articles, web development tutorials, web development guides, web development resources`,
		},
	]
}

export type Post = {
	title: string
	description: string
	imageUrl: string
	date: string
	href: string
	topics: string[]
	keywords: string[]
}

export const getPosts = () => {
	return [theImportanceOfUsingAReactUiLibrary, componentDesignAndBestPractices]
}

export const loader = () => {
	const posts: Post[] = getPosts()
	const tags = [...new Set(posts.map(({ topics }) => topics).flat())]
	return json({ posts, tags })
}

const Articles = () => {
	const loaderData = useLoaderData<typeof loader>()
	const [tagsArray, setTagsArray] = useState<{ tag: string; selected: boolean }[]>(loaderData.tags.map((tag: string) => ({ tag, selected: false })))
	const [postsArray, setPostsArray] = useState<Post[]>(loaderData.posts)

	const handleTagClick = (t: string) => {
		// Select the clicked tag
		const newTagsArray = tagsArray.map(({ tag, selected }) => ({ tag, selected: tag === t ? !selected : selected }))
		// Get all selected tags
		const selectedTags = newTagsArray.filter(({ selected }) => selected)
		// Filter posts by selected tags
		const newPostsArray = loaderData.posts.filter(({ topics }) => selectedTags.length === 0 || selectedTags.every(({ tag }) => topics.some(topic => tag === topic)))
		setTagsArray(newTagsArray)
		setPostsArray(newPostsArray)
	}

	return (
		<>
			<BackgroundBlur>
				<H1>Great web dev articles</H1>
				<Lead>Articles to make full-stack web dev a cakewalk.</Lead>
				{/* <div className="mx-auto mt-10 max-w-2xl lg:max-w-none">
							<div className="mt-6 flex max-w-md gap-x-4">
								<Label htmlFor="search" className="sr-only">
									Search articles by title or content
								</Label>
								<Input id="search" name="search" type="text" placeholder="Search articles by title or content" />
								<Button variant="primary" asChild>
									<Link
										to="/articles"
										className="flex-none rounded-md bg-brand px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
									>
										Search articles
									</Link>
								</Button>
							</div>
						</div> */}
				<Flex orientation="vertical" gap="4" className="bg-muted-100 rounded-lg p-4">
					<span className="text-muted-500 text-xs">Filter by topic</span>
					<div className="flex flex-wrap gap-4">
						{tagsArray.map(({ tag, selected }) => (
							<Button key={tag} variant={selected ? 'primary' : 'secondary'} size="sm" onPress={() => handleTagClick(tag)}>
								{tag}
							</Button>
						))}
					</div>
				</Flex>
				<div className="mt-4 mx-auto grid max-w-2xl auto-rows-fr grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
					{postsArray.map(post => (
						<ArticlePreview key={post.title} post={post} />
					))}
				</div>
				{/* <div className="flex justify-around">
						<Button variant="secondary" size="lg" className="mt-8 text-muted-600 sm:mt-10 lg:mt-12">
							Load more articles...
						</Button>
					</div> */}
			</BackgroundBlur>
		</>
	)
}

export default Articles
